"use client";

import { cn } from "@/src/lib/utils";
import React from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLocale } from "@/hooks/use-locale";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslations } from "next-intl";
import {
  useInterfaceLanguageOptions,
  useUiLanguageShortLabel,
} from "@/hooks/use-ui-language-options";
import { useUserRole } from "@/hooks/useUserRole";
import { usePathname } from "next/navigation";
import { Search } from "lucide-react";
import GlobalSearchDialog from "@/components/GlobalSearch/GlobalSearchDialog";
import {
  markGlobalSearchUsed,
  warmGlobalSearch,
} from "@/components/GlobalSearch/globalSearchApi";

const AppRightSidebar: React.FC = () => {
  const t = useTranslations();
  const { currentLocale, changeLanguage } = useLocale();
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const [handoff, setHandoff] = React.useState<{ query: string; session: boolean } | null>(null);
  const pathname = usePathname();

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const query = params.get("search");

    if (!query) return;

    setHandoff({ query, session: params.get("session") === "1" });
    setIsSearchOpen(true);
    markGlobalSearchUsed();

    params.delete("search");
    params.delete("session");

    const rest = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (rest ? `?${rest}` : ""));
  }, [pathname]);
  const isMobile = useIsMobile();
  const languageOptions = useInterfaceLanguageOptions();
  const languageShortLabel = useUiLanguageShortLabel(currentLocale);

  const { isStudent } = useUserRole();
  const warmSearch = React.useCallback(() => {
    if (isStudent) return;
    void warmGlobalSearch();
  }, [isStudent]);


  React.useEffect(() => {
    if (isStudent) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        markGlobalSearchUsed();
        setIsSearchOpen((prev) => !prev);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isStudent]);

  if (isMobile) {
    if (isStudent) return null;

    return (
      <>
        <div className="fixed right-4 bottom-4 z-40 flex flex-col gap-2">
          <button
            type="button"
            className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/80 bg-white/95 shadow-lg backdrop-blur transition-transform hover:scale-[1.02]"
            onClick={() => {
              markGlobalSearchUsed();
              setIsSearchOpen(true);
            }}
            onMouseEnter={warmSearch}
            onFocus={warmSearch}
            aria-label={t("label-global-search-title")}
            title={t("label-global-search-title")}
          >
            <Search className="h-5 w-5 text-gray-700" />
          </button>
        </div>

        <GlobalSearchDialog
          open={isSearchOpen}
          onOpenChange={setIsSearchOpen}
          handoff={handoff}
          onHandoffConsumed={() => setHandoff(null)}
        />
      </>
    );
  }

  return (
    <aside className={cn(isStudent ? "hidden md:flex" : "flex", "flex-col items-center justify-between bg-gradient-to-b from-white to-gray-50 border-r h-screen w-16 bg-[linear-gradient(0deg,rgba(255,255,255,.75),rgba(255,255,255,.75)),linear-gradient(270deg,rgba(219,178,158,.45)0%,rgba(219,189,172,.45)20.19%,rgba(210,195,187,.45)38.94%,rgba(175,187,207,.45)63.94%,rgba(134,149,180,.45)100%)]")}>
      <div className="flex flex-col items-center">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(
                "w-16 h-16 flex items-center justify-center text-lg font-semibold select-none border-b-[1.5px] border-gray-400",
                "bg-transparent text-gray-700 font-medium text-xs border-b-[1.5px] border-white",
                "cursor-pointer"
              )}
              aria-label="Language"
              title="Language"
            >
              {languageShortLabel}
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent side="left" align="start" className="min-w-[160px]">
            {languageOptions.map((opt) => (
              <DropdownMenuItem key={opt.locale} onSelect={() => changeLanguage(opt.locale)} className="cursor-pointer">
                <span className="mr-2 inline-flex min-w-[24px] justify-center text-xs font-semibold text-muted-foreground">
                  {opt.short}
                </span>
                <span className="text-sm">{opt.label}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex flex-col items-center">
        {!isStudent && (
          <button
            type="button"
            className="relative w-16 h-16 flex flex-col items-center justify-center gap-0.5 border-t-[1.5px] border-white cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={() => {
              markGlobalSearchUsed();
              setIsSearchOpen(true);
            }}
            onMouseEnter={warmSearch}
            onFocus={warmSearch}
            aria-label={t("label-global-search-title")}
            title={`${t("label-global-search-title")} (⌘K)`}
          >
            <Search className="h-5 w-5 text-gray-700" />
            <span className="text-[9px] font-medium text-gray-500">⌘K</span>
          </button>
        )}

        {!isStudent && (
          <GlobalSearchDialog
            open={isSearchOpen}
            onOpenChange={setIsSearchOpen}
            handoff={handoff}
            onHandoffConsumed={() => setHandoff(null)}
          />
        )}
      </div>
    </aside>
  );
};

export default AppRightSidebar;
